import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Settings as SettingsIcon, User, Shield, BookOpen, ExternalLink, Edit2, Save, X, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';

const APP_VERSION = 'v1.2.0';
const APP_BUILD_DATE = 'August 2026';

const DATA_TYPE_LABELS = {
  ap42_chapter1:   { label: 'AP-42 Chapter 1', icon: '📊', desc: 'External combustion emission factors' },
  ap42_chapter7:   { label: 'AP-42 Chapter 7', icon: '🛢️', desc: 'Storage tank emission factors (TANKS equivalent)' },
  rmp_oca:         { label: 'RMP OCA Guidance', icon: '☁️', desc: 'Offsite consequence analysis equations (RMP*Comp equivalent)' },
  aerscreen:       { label: 'AERSCREEN', icon: '🌬️', desc: 'Dispersion screening model' },
  egrid_2023:      { label: 'eGRID', icon: '⚡', desc: 'Electricity emission factors by subregion' },
  ghg_gwp:         { label: 'GHG Emission Factors', icon: '🌱', desc: 'Global warming potential values (IPCC AR5)' },
  iris:            { label: 'EPA IRIS', icon: '🏥', desc: 'Toxicity values for health risk screening' },
  nsps_neshap:     { label: 'NSPS / NESHAP', icon: '📋', desc: 'Federal air quality standards (40 CFR 60/61/63)' },
  tanks_chemicals: { label: 'Tank Chemicals Database', icon: '⚗️', desc: 'Chemical properties for TANKS calculations' },
};

const inp = { width:'100%', padding:'8px 12px', borderRadius:8, border:'1px solid #E2E8F0', fontFamily:'var(--font-body)', fontSize:13, boxSizing:'border-box' };
const lbl = { fontSize:11, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:3 };

export default function Settings() {
  const [activeTab, setActiveTab] = useState('account');
  const [user, setUser] = useState(null);
  const [versions, setVersions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [profileForm, setProfileForm] = useState({ name:'', email:'' });
  const [passwordForm, setPasswordForm] = useState({ current:'', new_password:'', confirm:'' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const userRes = await api.get('/auth/me');
      setUser(userRes.data.user);
      setProfileForm({ name: userRes.data.user.name || '', email: userRes.data.user.email || '' });
      if (userRes.data.user?.is_admin) {
        const verRes = await api.get('/versions');
        setVersions(verRes.data.versions || []);
      }
    } catch(e) { console.error(e); }
  };

  const currentVersions = {};
  const historyVersions = {};
  versions.forEach(v => {
    if (v.is_current) currentVersions[v.data_type] = v;
    else {
      if (!historyVersions[v.data_type]) historyVersions[v.data_type] = [];
      historyVersions[v.data_type].push(v);
    }
  });

  const saveProfile = async () => {
    try {
      setSaving(true);
      await api.put('/auth/profile', profileForm);
      toast.success('Profile updated');
      loadAll();
    } catch(e) { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const savePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm) { toast.error('Passwords do not match'); return; }
    try {
      setSaving(true);
      await api.put('/auth/password', { current_password: passwordForm.current, new_password: passwordForm.new_password });
      toast.success('Password updated');
      setPasswordForm({ current:'', new_password:'', confirm:'' });
    } catch(e) { toast.error(e.response?.data?.error || 'Failed to update password'); }
    finally { setSaving(false); }
  };

  const startEdit = (v) => {
    setEditing(v.data_type);
    setEditForm({
      version_label: v.version_label,
      source_document: v.source_document || '',
      source_url: v.source_url || '',
      effective_date: v.effective_date ? v.effective_date.split('T')[0] : '',
      notes: v.notes || '',
    });
  };

  const saveVersion = async (data_type) => {
    try {
      await api.post('/versions/update', { data_type, ...editForm });
      toast.success('Version updated');
      setEditing(null);
      loadAll();
    } catch(e) { toast.error('Failed to update'); }
  };

  // Staleness check — warn if >12 months since effective date
  const isStale = (v) => {
    if (!v?.effective_date) return false;
    const effDate = new Date(v.effective_date);
    const monthsOld = (new Date() - effDate) / (1000 * 60 * 60 * 24 * 30);
    return monthsOld > 12;
  };

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    ...(user?.is_admin ? [{ id: 'versions', label: 'Regulatory Data', icon: BookOpen }] : []),
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:20 }}>
        <SettingsIcon size={20} color="#0066FF" />
        <div>
          <h1 style={{ fontFamily:'var(--font-heading)', fontSize:20, fontWeight:800, color:'#0A1628', margin:0 }}>Settings</h1>
          <div style={{ fontSize:12, color:'#94a3b8', fontFamily:'var(--font-body)' }}>AirComply {APP_VERSION} · {APP_BUILD_DATE}</div>
        </div>
      </div>

      <div style={{ display:'flex', gap:20 }}>
        {/* Sidebar tabs */}
        <div style={{ width:180, flexShrink:0 }}>
          {tabs.map(tab => (
            <div key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ display:'flex', alignItems:'center', gap:9, padding:'9px 12px', borderRadius:8, marginBottom:4, cursor:'pointer',
                background: activeTab===tab.id ? '#EFF6FF' : 'transparent',
                color: activeTab===tab.id ? '#0066FF' : '#374151',
                fontFamily:'var(--font-body)', fontSize:13, fontWeight: activeTab===tab.id ? 600 : 400 }}>
              <tab.icon size={14} />
              {tab.label}
            </div>
          ))}

          {/* App version card */}
          <div style={{ marginTop:24, padding:'12px', background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:8 }}>
            <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)', marginBottom:4 }}>Version</div>
            <div style={{ fontSize:16, fontWeight:800, color:'#0A1628', fontFamily:'var(--font-heading)' }}>{APP_VERSION}</div>
            <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>{APP_BUILD_DATE}</div>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex:1, minWidth:0 }}>

          {/* ── ACCOUNT TAB ── */}
          {activeTab === 'account' && (
            <div className="card" style={{ padding:'24px' }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:15, fontWeight:700, color:'#0A1628', marginBottom:20 }}>Profile</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={lbl}>Full Name</label>
                  <input type="text" value={profileForm.name} onChange={e => setProfileForm(f => ({...f, name:e.target.value}))} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Email Address</label>
                  <input type="email" value={profileForm.email} onChange={e => setProfileForm(f => ({...f, email:e.target.value}))} style={inp} />
                </div>
              </div>
              {user?.is_admin && (
                <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 10px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:20, marginBottom:14 }}>
                  <Shield size={11} color="#0066FF" />
                  <span style={{ fontSize:11, fontWeight:700, color:'#0066FF', fontFamily:'var(--font-body)' }}>Administrator</span>
                </div>
              )}
              <button onClick={saveProfile} disabled={saving} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
                <Save size={13} /> {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          )}

          {/* ── SECURITY TAB ── */}
          {activeTab === 'security' && (
            <div className="card" style={{ padding:'24px' }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:15, fontWeight:700, color:'#0A1628', marginBottom:20 }}>Change Password</div>
              <div style={{ display:'flex', flexDirection:'column', gap:12, maxWidth:400, marginBottom:16 }}>
                <div>
                  <label style={lbl}>Current Password</label>
                  <input type="password" value={passwordForm.current} onChange={e => setPasswordForm(f => ({...f, current:e.target.value}))} style={inp} />
                </div>
                <div>
                  <label style={lbl}>New Password</label>
                  <input type="password" value={passwordForm.new_password} onChange={e => setPasswordForm(f => ({...f, new_password:e.target.value}))} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Confirm New Password</label>
                  <input type="password" value={passwordForm.confirm} onChange={e => setPasswordForm(f => ({...f, confirm:e.target.value}))} style={inp} />
                </div>
              </div>
              <button onClick={savePassword} disabled={saving} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
                <Shield size={13} /> {saving ? 'Saving...' : 'Update Password'}
              </button>
            </div>
          )}

          {/* ── REGULATORY DATA TAB (admin only) ── */}
          {activeTab === 'versions' && user?.is_admin && (
            <div>
              <div style={{ padding:'12px 16px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, marginBottom:16, fontSize:12, color:'#92400E', fontFamily:'var(--font-body)' }}>
                <strong>Admin only.</strong> This controls the regulatory citation text shown on all AirComply reports and calculations. When EPA publishes an update, update the version label here to match — then update the corresponding calculation factors in the backend code before deploying. The label change alone does not update any calculation data.
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {Object.keys(DATA_TYPE_LABELS).map(dataType => {
                  const meta = DATA_TYPE_LABELS[dataType];
                  const current = currentVersions[dataType];
                  const history = historyVersions[dataType] || [];
                  const stale = isStale(current);
                  const isEditing = editing === dataType;

                  return (
                    <div key={dataType} className="card" style={{ padding:'16px 18px' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', gap:10 }}>
                        <div style={{ fontSize:20, flexShrink:0 }}>{meta.icon}</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2, flexWrap:'wrap' }}>
                            <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628' }}>{meta.label}</div>
                            {stale ? (
                              <span style={{ fontSize:10, background:'#FFFBEB', color:'#D97706', padding:'2px 7px', borderRadius:20, fontFamily:'var(--font-body)', fontWeight:600, display:'flex', alignItems:'center', gap:3 }}>
                                <AlertTriangle size={9} /> Check for EPA update
                              </span>
                            ) : (
                              <span style={{ fontSize:10, background:'#F0FDF4', color:'#16A34A', padding:'2px 7px', borderRadius:20, fontFamily:'var(--font-body)', fontWeight:600, display:'flex', alignItems:'center', gap:3 }}>
                                <CheckCircle size={9} /> Current
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', marginBottom:8 }}>{meta.desc}</div>

                          {!isEditing ? (
                            <>
                              <div style={{ fontSize:12, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', marginBottom:4 }}>
                                {current?.version_label || 'Not set'}
                              </div>
                              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:8, flexWrap:'wrap' }}>
                                {current?.effective_date && (
                                  <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)' }}>
                                    Effective: {new Date(current.effective_date).toLocaleDateString('en-US', { year:'numeric', month:'long' })}
                                  </div>
                                )}
                                {current?.source_url && (
                                  <a href={current.source_url} target="_blank" rel="noreferrer"
                                    style={{ fontSize:11, color:'#0066FF', fontFamily:'var(--font-body)', textDecoration:'none', display:'flex', alignItems:'center', gap:3 }}>
                                    EPA Source <ExternalLink size={9} />
                                  </a>
                                )}
                              </div>
                              {history.length > 0 && (
                                <div style={{ fontSize:10, color:'#94a3b8', fontFamily:'var(--font-body)', marginBottom:6 }}>
                                  Previous: {history.map(h => h.version_label).join(' · ')}
                                </div>
                              )}
                              <button onClick={() => startEdit(current || { data_type: dataType, version_label:'', source_document:'', source_url:'', effective_date:'', notes:'' })}
                                style={{ display:'flex', alignItems:'center', gap:4, background:'none', border:'1px solid #E2E8F0', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:11, color:'#374151' }}>
                                <Edit2 size={10} /> Update Version
                              </button>
                            </>
                          ) : (
                            <div style={{ background:'#F8FAFC', border:'1px solid #0066FF', borderRadius:8, padding:'12px 14px' }}>
                              <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:8, marginBottom:10 }}>
                                <div>
                                  <label style={lbl}>Version Label (appears on all reports) *</label>
                                  <input type="text" value={editForm.version_label}
                                    onChange={e => setEditForm(f => ({...f, version_label:e.target.value}))}
                                    placeholder="e.g. AP-42, Fifth Edition, Chapter 7.1 (September 2024 revision)"
                                    style={inp} />
                                </div>
                                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                                  <div>
                                    <label style={lbl}>Effective Date</label>
                                    <input type="date" value={editForm.effective_date}
                                      onChange={e => setEditForm(f => ({...f, effective_date:e.target.value}))} style={inp} />
                                  </div>
                                  <div>
                                    <label style={lbl}>EPA Source URL</label>
                                    <input type="url" value={editForm.source_url}
                                      onChange={e => setEditForm(f => ({...f, source_url:e.target.value}))} style={inp} />
                                  </div>
                                </div>
                                <div>
                                  <label style={lbl}>Notes (optional)</label>
                                  <input type="text" value={editForm.notes}
                                    onChange={e => setEditForm(f => ({...f, notes:e.target.value}))} style={inp} />
                                </div>
                              </div>
                              <div style={{ display:'flex', gap:8 }}>
                                <button onClick={() => saveVersion(dataType)} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:4, fontSize:12 }}>
                                  <Save size={11} /> Save
                                </button>
                                <button onClick={() => setEditing(null)} style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 12px', borderRadius:7, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:12 }}>
                                  <X size={11} /> Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
